import React from "react";
import styled from "styled-components";
import ExitToApp from "@material-ui/icons/ExitToApp";
import momentTz from "moment-timezone";
import { RouteComponentProps, withRouter } from "react-router";
import { Link } from "@sonder/ui/source/styled/fragments/Link";
import { PlainButton } from "@sonder/ui/source/styled/fragments/PlainButton";
import { Cell } from "@sonder/ui/source/styled/fragments/Table";
import { selectBooking } from "checkouts/actions";
import {
  mapChannelToPlatform,
  mapCheckoutStatusToDisplayName,
  mapEciLcoStatusToDisplayName,
} from "checkouts/helpers";
import { useStore } from "checkouts/store";
import { useCurrentUserTimezone } from "core/hooks/useCurrentTimeZone";
import { GetInquiryCheckoutsConnectionsInquiry } from "core/queries/GetInquiryCheckouts";
import { formatAccountName, parseGraphQLId } from "tasks/helpers";
import { InquiryCheckoutStatus } from "typings/globalGQLTypes";

interface CheckoutRowProps extends RouteComponentProps {
  booking: GetInquiryCheckoutsConnectionsInquiry;
  loading?: boolean;
  lastRow?: boolean;
}

const StyledCheckOutCell = styled(Cell)`
  justify-content: flex-end;
`;

export const Row = withRouter(
  ({ booking, lastRow, loading }: CheckoutRowProps) => {
    const { dispatch } = useStore();
    const timezone = useCurrentUserTimezone();
    const {
      id,
      account,
      checkOutStatus,
      checkOutTime,
      guestActualDepartureTime,
      lateCheckOutRequest,
      unit,
    } = booking;

    const currentTime = momentTz.tz(timezone);
    // use `actualDepartureTime` if it exists, else, fallback to checkOutTime.
    const actualCheckoutTime =
      (guestActualDepartureTime?.actualDepartureTime &&
        momentTz.tz(guestActualDepartureTime.actualDepartureTime, timezone)) ||
      (checkOutTime && momentTz.tz(checkOutTime, timezone));

    const isCheckedOut = checkOutStatus === InquiryCheckoutStatus.checked_out;
    const isToday = actualCheckoutTime
      .clone()
      .startOf("day")
      .isSame(currentTime.startOf("day"));

    const canCheckOut = !isCheckedOut && isToday;

    const sharedCellProps = {
      disabled: loading || isCheckedOut,
      rowKey: id,
    };
    return (
      <>
        <Cell {...sharedCellProps} rowStart lastRow={lastRow}>
          <Link
            href={`${ADMIN_HOST}/units/${parseGraphQLId(unit.id)}`}
            target="_blank"
          >
            {unit.shortInternalRef}
          </Link>
        </Cell>
        <Cell {...sharedCellProps}>
          <Link
            href={`${ADMIN_HOST}/inquiries/${parseGraphQLId(id)}`}
            target="_blank"
          >
            {formatAccountName(account)}
          </Link>
        </Cell>
        <Cell {...sharedCellProps}>
          {actualCheckoutTime && actualCheckoutTime.format("h:mm A")}
        </Cell>
        <Cell {...sharedCellProps}>
          {mapCheckoutStatusToDisplayName(checkOutStatus)}
        </Cell>
        <Cell {...sharedCellProps}>
          {mapChannelToPlatform(guestActualDepartureTime?.channel)}
        </Cell>
        <Cell {...sharedCellProps}>
          {lateCheckOutRequest?.eciLcoRequestId && (
            <Link
              href={`${ADMIN_HOST}/tom/eci_lco_requests/${lateCheckOutRequest?.eciLcoRequestId}`}
              target="_blank"
            >
              {mapEciLcoStatusToDisplayName(lateCheckOutRequest?.status)}
            </Link>
          )}
        </Cell>
        <StyledCheckOutCell {...sharedCellProps} rowEnd lastRow={lastRow}>
          {canCheckOut ? (
            <PlainButton
              onClick={() =>
                requestAnimationFrame(() => dispatch(selectBooking(booking)))
              }
            >
              <ExitToApp />
            </PlainButton>
          ) : null}
        </StyledCheckOutCell>
      </>
    );
  },
);
